import React, { useEffect, useMemo, useState } from "react";
import { Loader } from "@mantine/core";
import { useSelector } from "react-redux";
import schedulerService from "../../../services/scheduler";
import { getAllFacilitiesModal } from "../../../services/facility";
import SchedulerAdmin from "./SchedulerAdmin";
import SchedulerStaff from "./SchedulerStaff";
import SetupWizard from "./SetupWizard";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { isAppInstalled } from "../../../utils/appEntitlements";

/**
 * Scheduler / Time & Attendance module (OD-923).
 * One route, two experiences:
 *   - admin/owner -> Scheduler (build & manage schedules)
 *   - staff       -> Time Management (own shifts + time clock)
 * Phase 1: module shell + role split + admin empty-state/setup entry.
 * Calendar, drawers, timesheets, settings land in later phases.
 */
export default function Scheduler() {
  const { profile } = useSelector((state) => state?.profile);
  const isStaff = profile?.user?.role === "staff";
  // Calendar is an opt-in app: block direct navigation to the module when the
  // org hasn't installed it (nav item is already hidden).
  const calendarInstalled = isAppInstalled(profile, "calendar");
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);

  // Facilities for the Setup Wizard's per-facility operating-hours & shift-template steps.
  const allFacilitiesRaw = useSelector((s) => s.facility?.allFacilities);
  const facilities = useMemo(() => {
    const list = allFacilitiesRaw?.facilities || (Array.isArray(allFacilitiesRaw) ? allFacilitiesRaw : []);
    return list.filter((x) => (x._id || x.id) && x.active === true).map((x) => ({ value: x._id || x.id, label: x.name }));
  }, [allFacilitiesRaw]);
  useEffect(() => {
    if (!isStaff && !facilities.length) getAllFacilitiesModal("all", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  useEffect(() => {
    let active = true;
    (async () => {
      const s = await schedulerService.getSchedulerSettings();
      if (active) {
        setSettings(s || null);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!calendarInstalled) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", minHeight: "60vh", textAlign: "center", padding: 24 }}>
        <Icon name="calendar" size={40} />
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--app-text)" }}>Calendar isn't installed</div>
        <div style={{ fontSize: 14, color: "var(--app-text-muted)", maxWidth: 360 }}>
          Install the Calendar app from Settings → Apps to schedule shifts and appointments.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: "60vh" }}>
        <Loader color="var(--app-primary)" />
      </div>
    );
  }

  // Admin, not yet set up -> animated empty state + setup entry point.
  if (!isStaff && !settings?.setupCompleted) {
    return (
      <div className={css.emptyWrap}>
        <div className={css.seBg}>
          <span className={`${css.seOrb} ${css.seOrb1}`} />
          <span className={`${css.seOrb} ${css.seOrb2}`} />
          <span className={`${css.seOrb} ${css.seOrb3}`} />
          <span className={css.seGrid} />
        </div>

        <div className={css.sePreview}>
          <div className={css.sePvCard}>
            <div className={css.sePvHead}>
              <span className={css.sePvDot} />
              <span className={css.sePvTitle}>This week</span>
              <span className={css.sePvPill}>Mon 8</span>
            </div>
            <div className={css.sePvGrid}>
              <span className={css.sePvCol} style={{ left: "33%" }} />
              <span className={css.sePvCol} style={{ left: "66%" }} />
              <span className={css.sePvLine} style={{ top: "25%" }} />
              <span className={css.sePvLine} style={{ top: "50%" }} />
              <span className={css.sePvLine} style={{ top: "75%" }} />
              <div className={`${css.sePvChip} ${css.teal}`} style={{ left: "3%", width: "28%", top: "9%", height: "42%", animationDelay: ".25s" }}>
                <b>Facility</b>
                <span>8:00–13:00</span>
              </div>
              <div className={`${css.sePvChip} ${css.info}`} style={{ left: "36%", width: "28%", top: "34%", height: "36%", animationDelay: ".4s" }}>
                <b>Client visit</b>
                <span>9:30–11:00</span>
              </div>
              <div className={`${css.sePvChip} ${css.violet}`} style={{ left: "69%", width: "28%", top: "15%", height: "30%", animationDelay: ".55s" }}>
                <b>Appointment</b>
                <span>1:00 PM</span>
              </div>
            </div>
          </div>
          <div className={`${css.seFloat} ${css.seAvatar}`}>CNA</div>
          <div className={`${css.seFloat} ${css.seClockpill}`}>
            <Icon name="check" size={13} /> Clocked in · 7:02
          </div>
        </div>

        <span className={css.seBadge}>
          <Icon name="rocket" size={13} /> New module
        </span>
        <h2 className={css.seH2}>Manage staff schedules and time, all in one place</h2>
        <p className={css.seLede}>
          Your Scheduler module is ready. It brings staff scheduling and time management together
          so you can build shifts, schedule appointments, and track clock-ins and hours worked
          across your facilities.
        </p>
        <div className={css.seFeats}>
          <div className={css.seFeat}>
            <span className={css.seFic} style={{ background: "var(--app-tint-blue)", color: "var(--app-ink-blue)" }}>
              <Icon name="calendar" size={17} />
            </span>
            <div>
              <div className={css.seFt}>Build schedules</div>
              <div className={css.seFs}>Shifts &amp; appointments</div>
            </div>
          </div>
          <div className={css.seFeat}>
            <span className={css.seFic} style={{ background: "var(--app-tint-green)", color: "#15803d" }}>
              <Icon name="clock" size={17} />
            </span>
            <div>
              <div className={css.seFt}>Track time</div>
              <div className={css.seFs}>Clock-in, EVV &amp; hours</div>
            </div>
          </div>
          <div className={css.seFeat}>
            <span className={css.seFic} style={{ background: "var(--app-tint-purple)", color: "var(--app-ink-purple)" }}>
              <Icon name="user" size={17} />
            </span>
            <div>
              <div className={css.seFt}>Manage staff</div>
              <div className={css.seFs}>By facility &amp; client</div>
            </div>
          </div>
        </div>
        <button className={css.seCta} onClick={() => setSetupOpen(true)} type="button">
          <Icon name="rocket" size={18} /> Set up Scheduler
        </button>
        <div className={css.seNote}>Takes about 3 minutes. You can change anything later in Settings.</div>

        <SetupWizard
          opened={setupOpen}
          onClose={() => setSetupOpen(false)}
          facilities={facilities}
          onComplete={() => {
            setSetupOpen(false);
            setSettings((prev) => ({ ...(prev || {}), setupCompleted: true }));
          }}
        />
      </div>
    );
  }

  // Admin Scheduler (calendar + create/detail drawers).
  if (!isStaff) return <SchedulerAdmin />;

  // Staff: My Shifts + Time Management.
  return <SchedulerStaff />;
}
